import React, { useEffect, useState } from "react";
import { Sparkle, LockKey } from "@phosphor-icons/react";
import { authMe, authLogin, UNAUTHORIZED_EVENT } from "@/lib/api";

/**
 * Pide la contraseña de acceso antes de mostrar la app. El servidor guarda una cookie de sesión
 * (30 días). Si en cualquier momento responde 401, vuelve a mostrar esta pantalla.
 */
export default function LoginGate({ children }) {
    const [state, setState] = useState("loading"); // loading | login | unconfigured | error | ready
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const check = async () => {
        setState("loading");
        try {
            const me = await authMe();
            if (!me.configured) setState("unconfigured");
            else setState(me.authenticated ? "ready" : "login");
        } catch {
            setState("error");
        }
    };

    useEffect(() => {
        check();
        const onUnauthorized = () => {
            setPassword("");
            setError("");
            setState("login");
        };
        window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
        return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    }, []);

    async function submit(e) {
        e.preventDefault();
        if (!password || busy) return;
        setBusy(true);
        setError("");
        try {
            await authLogin(password);
            setPassword("");
            setState("ready");
        } catch (err) {
            const status = err?.response?.status;
            if (status === 429) setError("Demasiados intentos. Espera unos minutos e inténtalo de nuevo.");
            else if (status === 401) setError("Contraseña incorrecta.");
            else if (status === 503) setState("unconfigured");
            else setError("No se pudo conectar con el servidor.");
        } finally {
            setBusy(false);
        }
    }

    if (state === "ready") return children;

    return (
        <div className="min-h-screen bg-brand-50 flex items-center justify-center p-6">
            <div className="w-full max-w-sm bg-white border border-brand-300 shadow-xl">
                <div className="px-6 pt-6 pb-4 flex items-center gap-3 border-b border-brand-200">
                    <div className="w-9 h-9 bg-brand-900 flex items-center justify-center">
                        <Sparkle size={20} weight="fill" className="text-brand-200" />
                    </div>
                    <div>
                        <h1 className="font-headings font-bold text-brand-950 leading-none text-lg">ZebraLab</h1>
                        <p className="text-[10px] text-brand-700 leading-none mt-1 font-mono uppercase tracking-widest">
                            by BoaIdeia
                        </p>
                    </div>
                </div>

                <div className="p-6">
                    {state === "loading" && (
                        <p data-testid="login-loading" className="text-sm text-brand-700 font-mono">
                            Cargando…
                        </p>
                    )}

                    {state === "error" && (
                        <div className="space-y-3">
                            <p data-testid="login-error-server" className="text-sm text-red-800">
                                No se pudo conectar con el servidor.
                            </p>
                            <button
                                onClick={check}
                                className="px-4 py-2 bg-brand-900 text-white text-sm font-medium hover:bg-brand-800"
                            >
                                Reintentar
                            </button>
                        </div>
                    )}

                    {state === "unconfigured" && (
                        <div data-testid="login-unconfigured" className="space-y-2 text-sm text-brand-900">
                            <p className="font-medium">El acceso aún no está configurado.</p>
                            <p className="text-brand-700">
                                Falta definir la variable <code className="font-mono bg-brand-100 px-1">ZEBRALAB_ACCESS_PASSWORD</code>{" "}
                                en el servidor. Por seguridad, hasta entonces el sitio permanece cerrado.
                            </p>
                        </div>
                    )}

                    {state === "login" && (
                        <form onSubmit={submit} className="space-y-4">
                            <div>
                                <label
                                    htmlFor="login-password"
                                    className="text-[10px] font-semibold text-brand-800 uppercase tracking-wider mb-1.5 flex items-center gap-1.5"
                                >
                                    <LockKey size={13} /> Contraseña de acceso
                                </label>
                                <input
                                    id="login-password"
                                    data-testid="login-password"
                                    type="password"
                                    autoFocus
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full bg-white border border-brand-300 text-brand-950 text-sm rounded-none px-3 py-2 focus:outline-none focus:border-brand-900"
                                />
                            </div>
                            {error && (
                                <div
                                    data-testid="login-error"
                                    className="bg-red-50 border border-red-300 text-red-800 text-sm px-3 py-2"
                                >
                                    {error}
                                </div>
                            )}
                            <button
                                data-testid="login-submit"
                                type="submit"
                                disabled={busy || !password}
                                className="w-full px-4 py-2 bg-brand-900 text-white text-sm font-medium hover:bg-brand-800 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {busy ? "Entrando…" : "Entrar"}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
